import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { APPT } from '@/constants/testIds';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = '19843779835'; // Shally's WhatsApp (E.164, no +)

const formatTime12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hr = ((h + 11) % 12) + 1;
  return `${hr}:${String(m || 0).padStart(2, '0')} ${period}`;
};

const buildWhatsAppUrl = (f) => {
  const text = `Hi Trendy Closet! ✨

I'd like to book an appointment.

👤 Name: ${f.name}
📞 Phone: ${f.phone}
📅 Date: ${f.preferred_date}
⏰ Time: ${formatTime12h(f.preferred_time)}
🎉 Occasion: ${f.occasion}${f.message ? `\n📝 Note: ${f.message}` : ''}

Please confirm if this works. Thank you!`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
};

const occasions = [
  'Wedding',
  'Reception',
  'Engagement',
  'Sangeet / Mehndi',
  'Festival',
  'Party',
  'Photoshoot',
  'Other',
];

export const AppointmentForm = () => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    occasion: '',
    message: '',
  });

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.preferred_date || !form.preferred_time || !form.occasion) {
      toast.error('Please fill in all required fields.');
      return;
    }
    const waUrl = buildWhatsAppUrl(form);
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    toast.success('WhatsApp opened — tap SEND to finish your booking.');
  };

  const inputCls =
    'w-full bg-transparent border-b border-brandborder focus:border-maroon outline-none py-3 text-charcoal placeholder-charcoal/40 font-sans transition-colors duration-300';

  return (
    <section id="appointment" data-testid="appointment-section" className="py-24 lg:py-32 bg-surface relative">
      <div className="container-luxe grid lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <span className="overline">Private Appointment</span>
          <h2 className="font-display text-4xl lg:text-5xl text-charcoal mt-3 leading-tight">
            Step inside, <em className="text-maroon not-italic font-medium">unhurried</em>.
          </h2>
          <div className="mt-5 h-px w-16 bg-gold" />
          <p className="mt-7 text-charcoal/75 leading-relaxed">
            Tell us about your occasion and pick a time that suits you. Shally will personally curate
            looks ahead of your visit so the moment you walk in, your edit is already waiting.
          </p>

          <ul className="mt-10 space-y-4 text-sm text-charcoal/80">
            <li className="flex gap-3"><span className="text-gold">●</span> Free 60-minute styling consultation</li>
            <li className="flex gap-3"><span className="text-gold">●</span> Tailoring & alterations available</li>
            <li className="flex gap-3"><span className="text-gold">●</span> Bridal & group bookings welcome</li>
            <li className="flex gap-3"><span className="text-gold">●</span> Open daily from 4:30 PM to 8:30 PM</li>
          </ul>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={submit}
          data-testid={APPT.form}
          className="lg:col-span-7 bg-ivory border border-brandborder p-8 md:p-12 shadow-luxe"
        >
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            <div className="sm:col-span-1">
              <label className="overline">Full Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={update('name')}
                data-testid={APPT.name}
                placeholder="Jane Doe"
                className={inputCls}
                required
              />
            </div>
            <div className="sm:col-span-1">
              <label className="overline">Phone *</label>
              <input
                type="tel"
                value={form.phone}
                onChange={update('phone')}
                data-testid={APPT.phone}
                placeholder="984-377-9835"
                className={inputCls}
                required
              />
            </div>
            <div className="sm:col-span-1">
              <label className="overline">Preferred Date *</label>
              <input
                type="date"
                value={form.preferred_date}
                onChange={update('preferred_date')}
                data-testid={APPT.date}
                className={inputCls}
                required
              />
            </div>
            <div className="sm:col-span-1">
              <label className="overline">Preferred Time *</label>
              <input
                type="time"
                value={form.preferred_time}
                onChange={update('preferred_time')}
                data-testid="appt-time"
                className={inputCls}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline">Occasion *</label>
              <select
                value={form.occasion}
                onChange={update('occasion')}
                data-testid={APPT.occasion}
                className={`${inputCls} appearance-none`}
                required
              >
                <option value="" disabled>Select your occasion</option>
                {occasions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="overline">Message</label>
              <textarea
                value={form.message}
                onChange={update('message')}
                data-testid={APPT.message}
                rows={4}
                placeholder="Tell us about your vision — colours, budget, sizes…"
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <p className="text-xs text-charcoal/60">Tapping Request Appointment opens a WhatsApp chat with your details pre-filled — just hit SEND.</p>
            <button
              type="submit"
              data-testid={APPT.submit}
              className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-[#25D366] hover:bg-[#1ebe5b] text-white text-xs uppercase tracking-[0.22em] transition-colors duration-500 shadow-card"
            >
              <MessageCircle className="w-4 h-4" /> Request Appointment
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default AppointmentForm;
